import { $generateHtmlFromNodes, initialPrefillTag, RichTextEditor } from '@talentmesh/rte';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import ProcessingState from '@/Models/ProcessingState';
import UIStrings from '@/Utils/UIStrings';
import { Label } from '@/Components/shadcn/label';
import { JobAdFieldTypes, JobAdFormValues } from '../JobAdStepUtils';
import CompanyDescriptionWings from './CompanyDescriptionWings.svg';
import RTELoadingOverlay from './RTELoadingOverlay';
import { cn } from '@/lib/utils';

interface JobAdCompanyDescriptionProps {
    initialHtml: string;
    processingState: ProcessingState;
    showLoadingOverlay?: boolean;
}

function JobAdCompanyDescription({
    initialHtml,
    processingState,
    showLoadingOverlay = false,
}: JobAdCompanyDescriptionProps): React.JSX.Element {
    const loading = processingState === ProcessingState.Processing;
    const { formState, setValue } = useFormContext<JobAdFormValues>();
    const { isSubmitting, touchedFields, errors } = formState;
    const isTouched = touchedFields.companyDescription;
    const errorMsg = errors.companyDescription?.message;

    return (
        <div className="flex flex-col gap-2.5">
            <Label>{UIStrings.CompanyDescription}</Label>
            <div className="flex h-86 flex-col" data-cy="CompanyDescriptionRTE">
                <RichTextEditor
                    editable={!isSubmitting}
                    loading={loading && showLoadingOverlay}
                    initialHtml={initialHtml}
                    loadingOverlay={
                        <RTELoadingOverlay dataCy="CompanyDescriptionLoadingOverlay" svg={CompanyDescriptionWings} />
                    }
                    data-cy={JobAdFieldTypes.CompanyDescription}
                    features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}
                    onChange={(editorState, editor, tags) => {
                        const isPrefill = tags.has(initialPrefillTag);
                        editorState.read(() => {
                            setValue('companyDescription', $generateHtmlFromNodes(editor), {
                                shouldTouch: !isPrefill,
                                shouldDirty: !isPrefill,
                                shouldValidate: true,
                            });
                        });
                    }}
                    toolbarProps={{
                        className: 'bg-card',
                    }}
                    contentEditableProps={{
                        className: cn(
                            'flex-1 overflow-y-auto',
                            isTouched && errorMsg ? 'border-destructive' : 'border-border',
                        ),
                    }}
                />
                {!loading && errorMsg && <p className="text-destructive mt-1 text-sm">{String(errorMsg)}</p>}
            </div>
        </div>
    );
}

export default JobAdCompanyDescription;
